import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useBlockId, useHref, useMedia, useRender, useT } from '../context';
import { pushDataLayer, readConsent, saveConsent, applyConsent } from '../tracking';
import { Carousel } from './Carousel';
import { Img, RichHtml, SmartLink, Stars, TitleHighlight } from './common';

// ---------- PageBanner ----------
export interface PageBannerProps {
  title: string;
  highlight: string;
  backgroundMediaId?: string | null;
  showBreadcrumb: boolean;
}

export function PageBanner({ title, highlight, backgroundMediaId, showBreadcrumb }: PageBannerProps) {
  const bg = useMedia(backgroundMediaId);
  const { page, lang } = useRender();
  const t = useT();
  const home = lang === 'de' ? '/' : `/${lang}`;
  return (
    <section
      className="page-banner"
      style={bg ? { backgroundImage: `url(${bg.url})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center center', backgroundSize: 'cover' } : undefined}
    >
      <div className="container">
        <div className="content">
          <h1 className="kfz-banner-title">
            <TitleHighlight title={title} highlight={highlight} />
          </h1>
          {showBreadcrumb ? (
            <ul className="breadcumb">
              <li>
                <a href={home}>{t('start')}</a>
                <i className="fa fa-chevron-right" aria-hidden="true" />
              </li>
              <li>
                <a className="active" href={page?.path ?? '#'} aria-current="page">
                  {page?.title ?? highlight}
                </a>
              </li>
            </ul>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ---------- ServiceTabs ----------
export interface ServiceTabItem {
  key: string;
  iconMediaId?: string | null;
  tabLabel: string;
  heading: string;
  bodyHtml: string;
  bullets: string[];
  imageMediaId?: string | null;
  buttonLabel?: string;
  buttonHref?: string;
}

export interface ServiceTabsProps {
  title: string;
  highlight: string;
  items: ServiceTabItem[];
}

export function ServiceTabs({ title, highlight, items }: ServiceTabsProps) {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const safeActive = Math.min(active, Math.max(0, items.length - 1));

  const onKey = (e: KeyboardEvent, i: number) => {
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % items.length;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + items.length) % items.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = items.length - 1;
    if (next >= 0) {
      e.preventDefault();
      setActive(next);
      tabRefs.current[next]?.focus();
    }
  };

  return (
    <section className="our-service">
      <div className="container">
        <div className="sec-title">
          <h2>
            <TitleHighlight title={title} highlight={highlight} />
          </h2>
        </div>
        <div className="item-list">
          <div className="row">
            <div className="outer-box">
              <ul className="tab-list" role="tablist">
                {items.map((item, i) => (
                  <li key={item.key} className={`item hvr-bubble-bottom${i === safeActive ? ' active' : ''}`} role="presentation">
                    <a
                      ref={(el) => {
                        tabRefs.current[i] = el;
                      }}
                      href={`#${item.key}`}
                      id={`tab-${item.key}`}
                      role="tab"
                      aria-controls={item.key}
                      aria-selected={i === safeActive}
                      tabIndex={i === safeActive ? 0 : -1}
                      className="clearfix"
                      onClick={(e) => {
                        e.preventDefault();
                        setActive(i);
                      }}
                      onKeyDown={(e) => onKey(e, i)}
                    >
                      <i>
                        <Img mediaId={item.iconMediaId} width={34} height={34} decorative />
                      </i>
                      {item.tabLabel.split('\n').map((line, n) => (
                        <h4 key={n}>{line}</h4>
                      ))}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="tab-content">
              {items.map((item, i) => (
                <div
                  key={item.key}
                  className={`inner-box tab-pane fade${i === safeActive ? ' in active' : ''}`}
                  id={item.key}
                  role="tabpanel"
                  aria-labelledby={`tab-${item.key}`}
                >
                  <div className="item-list">
                    <div className="row">
                      <div className="col-md-7 col-sm-6 col-xs-12">
                        <Img mediaId={item.imageMediaId} />
                      </div>
                      <div className="col-md-5 col-sm-6 col-xs-12">
                        <div className="content-box">
                          <h3>{item.heading}</h3>
                          <RichHtml html={item.bodyHtml} className="kfz-rich" />
                          {item.bullets.length > 0 ? (
                            <ul>
                              {item.bullets.map((b, n) => (
                                <li key={n}>
                                  <span className="kfz-bullet">{b}</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {item.buttonLabel ? (
                            <div className="link-btn">
                              <SmartLink className="thm-btn" href={item.buttonHref}>
                                {item.buttonLabel}
                              </SmartLink>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Features ----------
export interface FeaturesProps {
  items: { title: string; lines: { text: string; href?: string }[] }[];
}

export function Features({ items }: FeaturesProps) {
  return (
    <section className="features-two">
      <div className="container">
        <div className="row">
          {items.map((item, i) => (
            <div key={i} className="col-md-4 col-sm-6 col-xs-12">
              <div className="content">
                <h4>{item.title}</h4>
                <div className="text">
                  {item.lines.map((line, n) => (
                    <p key={n}>
                      {line.href ? (
                        <SmartLink href={line.href} className="kfz-feature-link">
                          {line.text}
                        </SmartLink>
                      ) : (
                        line.text
                      )}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- CTA (subscribe-us) ----------
export interface CtaProps {
  heading: string;
  buttonLabel?: string;
  buttonHref?: string;
}

export function CTA({ heading, buttonLabel, buttonHref }: CtaProps) {
  const hasButton = Boolean(buttonLabel);
  return (
    <section className="subscribe-us">
      <div className="container">
        <div className="row">
          <div className={hasButton ? 'col-md-9' : 'col-md-12'}>
            <h3>{heading}</h3>
          </div>
          {hasButton ? (
            <div className="col-md-3">
              <SmartLink href={buttonHref} className="thm-btn inverse pull-right">
                {buttonLabel}
              </SmartLink>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ---------- Testimonials ----------
export interface TestimonialsProps {
  title: string;
  highlight: string;
  items: { avatarMediaId?: string | null; name: string; href?: string; rating: number; text: string }[];
}

export function Testimonials({ title, highlight, items }: TestimonialsProps) {
  const t = useT();
  return (
    <section className="testimonial">
      <div className="container">
        <div className="sec-title">
          <h2>
            <TitleHighlight title={title} highlight={highlight} />
          </h2>
        </div>
        <Carousel
          className="testimonial-carousel"
          label={`${title}${highlight}`}
          gap={30}
          breakpoints={[
            { minWidth: 0, items: 1 },
            { minWidth: 740, items: 2 },
            { minWidth: 1000, items: 3 },
          ]}
          items={items.map((item, i) => (
            <div className="item" key={i}>
              <div className="image-box">
                <Img mediaId={item.avatarMediaId} decorative />
              </div>
              <div className="text">
                <h4>
                  {item.href ? (
                    <SmartLink href={item.href} className="kfz-testimonial-name">
                      {item.name}
                    </SmartLink>
                  ) : (
                    item.name
                  )}
                </h4>
                <Stars rating={item.rating} label={t('ratingLabel', { n: item.rating })} />
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        />
      </div>
    </section>
  );
}

// ---------- Team ----------
export interface TeamProps {
  title: string;
  highlight: string;
  members: { photoMediaId?: string | null; name: string; role: string; links: { network: string; href: string }[] }[];
}

const networkIcon: Record<string, string> = {
  instagram: 'fa-instagram',
  facebook: 'fa-facebook-f',
  linkedin: 'fa-linkedin',
  xing: 'fa-xing',
  twitter: 'fa-twitter',
  website: 'fa-globe',
};

export function Team({ title, highlight, members }: TeamProps) {
  return (
    <section className="our-team">
      <div className="container">
        <div className="sec-title">
          <h2>
            <TitleHighlight title={title} highlight={highlight} />
          </h2>
        </div>
        <Carousel
          className="team-carousel"
          label={`${title} ${highlight}`}
          gap={30}
          autoplayMs={3000}
          speedMs={2000}
          breakpoints={[
            { minWidth: 0, items: 1 },
            { minWidth: 540, items: 2 },
            { minWidth: 1000, items: 4 },
          ]}
          items={members.map((m, i) => (
            <div className="item" key={i}>
              <figure className="image-holder">
                <Img mediaId={m.photoMediaId} altOverride={m.name} />
                {m.links.length > 0 ? (
                  <ul className="thm-btn hvr-bounce-to-top">
                    {m.links.map((l, n) => (
                      <li key={n}>
                        <SmartLink href={l.href} ariaLabel={`${m.name} – ${l.network}`}>
                          <span className={`fa ${networkIcon[l.network] ?? 'fa-link'}`} aria-hidden="true" />
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </figure>
              <div className="content-holder">
                <h4>{m.name}</h4>
                <h5>{m.role}</h5>
              </div>
            </div>
          ))}
        />
      </div>
    </section>
  );
}

// ---------- Article (single-blog-post) ----------
export interface ArticleProps {
  heading: string;
  subheading?: string;
  imageMediaId?: string | null;
  introHtml: string;
  quote?: string;
  bodyHtml?: string;
}

export function Article({ heading, subheading, imageMediaId, introHtml, quote, bodyHtml }: ArticleProps) {
  return (
    <article className="single-blog-post style-two">
      <div className="post-inner">
        {imageMediaId ? (
          <div className="image-box">
            <figure>
              <Img mediaId={imageMediaId} />
            </figure>
          </div>
        ) : null}
        <div className="post-header">
          <h2>{heading}</h2>
          {subheading ? <h3>{subheading}</h3> : null}
        </div>
        <div className="post-desc">
          <RichHtml html={introHtml} className="text kfz-rich" />
        </div>
        {quote ? (
          <blockquote>
            {quote}
            <div className="quate">
              <i className="fa fa-quote-right" aria-hidden="true" />
            </div>
          </blockquote>
        ) : null}
        {bodyHtml ? <RichHtml html={bodyHtml} className="text kfz-rich" /> : null}
      </div>
    </article>
  );
}

// ---------- RichText ----------
export function RichText({ html }: { html: string }) {
  return <RichHtml html={html} className="kfz-rich kfz-richtext" />;
}

// ---------- ImageText (about-seciton) ----------
export interface ImageTextProps {
  heading: string;
  highlight: string;
  html: string;
  imageMediaId?: string | null;
  imagePosition: 'right' | 'left';
}

export function ImageText({ heading, highlight, html, imageMediaId, imagePosition }: ImageTextProps) {
  const text = (
    <div className="col-md-6 col-sm-6 col-xs-12" key="text">
      <div className="single-item">
        <h2>
          <TitleHighlight title={heading} highlight={highlight} />
        </h2>
        <RichHtml html={html} className="kfz-rich" />
      </div>
    </div>
  );
  const image = (
    <div className="col-md-6 col-sm-6 col-xs-12" key="image">
      <div className="single-item">
        <figure className="image-box">
          <Img mediaId={imageMediaId} />
        </figure>
      </div>
    </div>
  );
  return (
    <section className="about-seciton">
      <div className="container">
        <div className="row">{imagePosition === 'left' ? [image, text] : [text, image]}</div>
      </div>
    </section>
  );
}

// ---------- Counters (fact-counter) ----------
export interface CountersProps {
  backgroundMediaId?: string | null;
  items: { icon: string; value: number; suffix?: string; label: string }[];
}

function CountUp({ value }: { value: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / 3000);
        setShown(Math.floor(value * p));
        if (p < 1) frame = requestAnimationFrame(tick);
      };
      setShown(0);
      frame = requestAnimationFrame(tick);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value]);
  return (
    <span ref={ref} className="count-text">
      {shown}
    </span>
  );
}

export function Counters({ backgroundMediaId, items }: CountersProps) {
  const bg = useMedia(backgroundMediaId);
  const width = items.length > 0 ? Math.max(3, Math.floor(12 / items.length)) : 4;
  return (
    <section className="fact-counter" style={bg ? { backgroundImage: `url(${bg.url})` } : undefined}>
      <div className="container">
        <div className="row">
          <div className="counter-outer">
            {items.map((item, i) => (
              <article key={i} className={`column counter-column col-md-${width} col-sm-6 col-xs-12`}>
                <div className="item">
                  <div className="inner-box">
                    <div className="icon-box">
                      <i className={`icon ${item.icon}`} aria-hidden="true" />
                    </div>
                    <div className="count-outer">
                      <CountUp value={item.value} />
                      {item.suffix}
                      <h5>{item.label}</h5>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- ContactInfo ----------
export interface ContactInfoProps {
  items: { icon: 'location' | 'phone' | 'email'; text: string; href?: string; subText?: string; tracking?: string }[];
}

const contactIcon = { location: 'flaticon-location', phone: 'flaticon-smartphone-call', email: 'flaticon-message' };

export function ContactInfo({ items }: ContactInfoProps) {
  return (
    <section className="contact-info-area">
      <div className="container">
        <div className="row">
          {items.map((item, i) => (
            <div key={i} className="col-md-4 col-sm-6 col-xs-12">
              <div className="contact-info">
                <div className="icon-box">
                  <i className={contactIcon[item.icon]} aria-hidden="true" />
                </div>
                <h5>
                  <SmartLink
                    href={item.href}
                    className="kfz-contact-link"
                    tracking={(item.tracking || null) as never}
                    trackingLocation={`contact_info_${item.icon}`}
                  >
                    {item.text}
                  </SmartLink>
                </h5>
                {item.subText ? <span>{item.subText}</span> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- MapEmbed ----------
export interface MapEmbedProps {
  mode: 'embedUrl' | 'query';
  embedUrl?: string;
  query?: string;
  height: number;
}

/** Google Maps is only loaded after marketing consent (the legacy site loaded it unconditionally). */
export function MapEmbed({ height, query, embedUrl }: MapEmbedProps) {
  const { resources, mode, site } = useRender();
  const t = useT();
  const [allowed, setAllowed] = useState(false);
  const blockId = useBlockId();
  const src = (blockId && resources.maps[blockId]) || embedUrl || (query ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : '');

  useEffect(() => {
    setAllowed(readConsent()?.marketing === true);
    const onConsent = (e: Event) => setAllowed((e as CustomEvent).detail?.marketing === true);
    window.addEventListener('kfz:consent', onConsent);
    return () => window.removeEventListener('kfz:consent', onConsent);
  }, []);

  return (
    <section className="contact_details sec-padd">
      {allowed && src && mode !== 'editor' ? (
        <iframe
          src={src}
          width="100%"
          height={height}
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Google Maps"
        />
      ) : (
        <div className="kfz-map-placeholder" style={{ height }}>
          <div className="kfz-map-placeholder__inner">
            <i className="fa fa-map-marker" aria-hidden="true" />
            <p>{t('mapConsent')}</p>
            <button
              type="button"
              className="thm-btn"
              onClick={() => {
                const consent = { analytics: readConsent()?.analytics === true, marketing: true };
                saveConsent(consent);
                applyConsent(consent);
                setAllowed(true);
              }}
            >
              {t('mapLoad')}
            </button>
            {site?.addressMapsUrl ? (
              <p>
                <a href={site.addressMapsUrl} target="_blank" rel="noopener noreferrer">
                  {t('mapOpen')}
                </a>
              </p>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- TrackingEvent ----------
export interface TrackingEventProps {
  event: string;
  params: { key: string; value: string }[];
}

/** Pushes a dataLayer event when the page is viewed (e.g. lead_form_submit on the thank-you page). */
export function TrackingEvent({ event, params }: TrackingEventProps) {
  const { mode } = useRender();
  useEffect(() => {
    if (mode === 'editor') return;
    pushDataLayer({ event, ...Object.fromEntries(params.map((p) => [p.key, p.value])) });
  }, [event, params, mode]);
  if (mode === 'editor') return <div className="kfz-editor-note">dataLayer: {event}</div>;
  return null;
}

export { useHref };
